import { useState, useRef, useEffect } from 'react';
import styles from './heygencss.js';

// Configuration constants
const API_CONFIG = {
  apiKey: "NTE1ODQ2MGQ0ZDc5NGExMWFiNTA5NjU2M2QyNThlNWUtMTc2MDQyNDYwOA==",
  serverUrl: "https://api.heygen.com",
};

const LIVEKIT_CONFIG = {
  serverUrl: "wss://demo-2oek58i9.livekit.cloud",
  apiKey: "API9nQmw78EmAiB",
  apiSecret: "AADv30TXtf45oAmC1LBreUj42codwewyLVOZiescz18C",
};

// Default values
const DEFAULT_AVATAR_ID = "Wayne_20240711";
const DEFAULT_OPENING_TEXT = "Hello, how can I help you?";
const DEFAULT_STT_LANGUAGE = "en";
const DEFAULT_VIDEO_QUALITY = "high";
const DEFAULT_VIDEO_ENCODING = "H264";
const DEFAULT_VOICE_RATE = 1.0;


const HeygenStreamingComponent = () => {
  // State variables
  const [avatarID, setAvatarID] = useState(DEFAULT_AVATAR_ID);
  const [voiceID, setVoiceID] = useState("2d5b0e6cf36f460aa7fc47e3eee4ba54");
  const [taskInput, setTaskInput] = useState("");
  const [statusLogs, setStatusLogs] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Refs for persistent data
  const sessionInfoRef = useRef(null);
  const roomRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const webSocketRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const mediaElementRef = useRef(null);

  // Load LiveKit client script
  useEffect(() => {
    const loadLiveKitClient = () => {
      if (window.LivekitClient) {
        updateStatus("✓ LiveKit client already loaded");
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/livekit-client@latest/dist/livekit-client.umd.min.js';
      script.onload = () => {
        updateStatus("✓ LiveKit client loaded successfully");
      };
      script.onerror = () => {
        updateStatus("✗ Failed to load LiveKit client");
      };
      document.head.appendChild(script);
    };

    loadLiveKitClient();
  }, []);

  // Helper function to update status
  const updateStatus = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] ${message}`;
    
    setStatusLogs(prevLogs => [logEntry, ...prevLogs].slice(0, 100)); // Keep last 100 logs
    console.log(message);
  };

  // Generate LiveKit token
  const generateLiveKitToken = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      updateStatus("Generating LiveKit token...");
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/assistant/create-call-token/?assistantId=12322727-ac18-4e8d-9eee-a3b3ecb08cd5`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error("Failed to fetch token");
      }
      
      const data = await response.json();
      updateStatus("✓ LiveKit token generated");
      return data.token;
    } catch (error) {
      updateStatus(`✗ Token generation error: ${error.message}`);
      throw error;
    }
  };

  // Get session token from HeyGen
  const getSessionToken = async () => {
    try {
      updateStatus("Getting session token from HeyGen...");
      const response = await fetch(
        `${API_CONFIG.serverUrl}/v1/streaming.create_token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": API_CONFIG.apiKey,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      sessionTokenRef.current = data.data.token;
      updateStatus("✓ Session token obtained");
      return true;
    } catch (error) {
      updateStatus(`✗ Error getting session token: ${error.message}`);
      throw error;
    }
  };

  // Connect WebSocket to HeyGen
  const connectWebSocket = async (sessionId) => {
    return new Promise((resolve, reject) => {
      try {
        updateStatus("Connecting WebSocket to HeyGen...");
        const params = new URLSearchParams({
          session_id: sessionId,
          session_token: sessionTokenRef.current,
          silence_response: false,
          opening_text: DEFAULT_OPENING_TEXT,
          stt_language: DEFAULT_STT_LANGUAGE,
        });

        const wsUrl = `wss://${new URL(API_CONFIG.serverUrl).hostname}/v1/ws/streaming.chat?${params}`;

        webSocketRef.current = new WebSocket(wsUrl);

        webSocketRef.current.addEventListener("message", (event) => {
          const eventData = JSON.parse(event.data);
          console.log("WebSocket message:", eventData);
          updateStatus(`WebSocket: ${eventData.type || 'message received'}`);
        });

        webSocketRef.current.addEventListener("error", (error) => {
          updateStatus(`✗ WebSocket error: ${error}`);
          reject(error);
        });

        webSocketRef.current.addEventListener("open", () => {
          updateStatus("✓ WebSocket connection opened");
          resolve();
        });

        webSocketRef.current.addEventListener("close", () => {
          updateStatus("WebSocket connection closed");
        });

        // Timeout after 10 seconds
        setTimeout(() => {
          reject(new Error("WebSocket connection timeout"));
        }, 10000);

      } catch (error) {
        updateStatus(`✗ WebSocket connection failed: ${error.message}`);
        reject(error);
      }
    });
  };

  // Initialize LiveKit Room
  const initializeLiveKitRoom = async () => {
    try {
      updateStatus("Initializing LiveKit room...");
      
      // Wait for LivekitClient to be available
      let retries = 0;
      while (!window.LivekitClient && retries < 10) {
        await new Promise(resolve => setTimeout(resolve, 500));
        retries++;
      }

      if (!window.LivekitClient) {
        throw new Error("LivekitClient not found after waiting");
      }

      const { Room, VideoPresets, RoomEvent } = window.LivekitClient;
      
      // Create new room instance
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: {
          resolution: VideoPresets.h720.resolution,
        },
      });

      // Handle media streams
      const mediaStream = new MediaStream();
      mediaStreamRef.current = mediaStream;

      // Set up event listeners
      room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        if (track.kind === "video" || track.kind === "audio") {
          mediaStream.addTrack(track.mediaStreamTrack);
          updateStatus(`✓ ${track.kind} track subscribed`);
          
          if (mediaElementRef.current) {
            mediaElementRef.current.srcObject = mediaStream;
            updateStatus("✓ Media stream set on video element");
            
            // Play the video
            mediaElementRef.current.play().catch(e => {
              console.log("Auto-play prevented:", e);
              updateStatus("Note: Click the video to enable playback");
            });
          }
        }
      });

      room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
        const mediaTrack = track.mediaStreamTrack;
        if (mediaTrack) {
          mediaStream.removeTrack(mediaTrack);
          updateStatus(`Track unsubscribed: ${track.kind}`);
        }
      });

      room.on(RoomEvent.Connected, () => {
        updateStatus("✓ LiveKit room connected successfully");
        setIsConnected(true);
      });

      room.on(RoomEvent.Disconnected, (reason) => {
        updateStatus(`LiveKit room disconnected: ${reason}`);
        setIsConnected(false);
        setIsInitialized(false);
      });

      roomRef.current = room;
      updateStatus("✓ LiveKit room initialized");
      return room;
    } catch (error) {
      updateStatus(`✗ LiveKit room initialization failed: ${error.message}`);
      throw error;
    }
  };

  // Prepare LiveKit connection
  const prepareLiveKitConnection = async (room) => {
    try {
      updateStatus("Preparing LiveKit connection...");
      const liveKitToken = await generateLiveKitToken();
      
      await room.prepareConnection(LIVEKIT_CONFIG.serverUrl, liveKitToken);
      updateStatus("✓ LiveKit connection prepared");
      return true;
    } catch (error) {
      updateStatus(`✗ LiveKit connection preparation failed: ${error.message}`);
      throw error;
    }
  };

  // Create new session
  const createNewSession = async () => {
    try {
      if (!sessionTokenRef.current) {
        await getSessionToken();
      }

      updateStatus("Creating HeyGen session...");
      const response = await fetch(
        `${API_CONFIG.serverUrl}/v1/streaming.new`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionTokenRef.current}`,
          },
          body: JSON.stringify({
            quality: DEFAULT_VIDEO_QUALITY,
            avatar_name: avatarID,
            voice: {
              voice_id: voiceID,
            },
            version: "v2",
            video_encoding: DEFAULT_VIDEO_ENCODING,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, details: ${errorText}`);
      }

      const data = await response.json();
      sessionInfoRef.current = data.data;
      updateStatus("✓ HeyGen session created");

      // Initialize LiveKit room
      const room = await initializeLiveKitRoom();
      
      // Prepare LiveKit connection
      await prepareLiveKitConnection(room);

      // Connect WebSocket to HeyGen
      await connectWebSocket(sessionInfoRef.current.session_id);

      setIsInitialized(true);
      updateStatus("✓ Session setup complete - ready to start streaming");
      return true;
    } catch (error) {
      updateStatus(`✗ Error creating session: ${error.message}`);
      setIsInitialized(false);
      throw error;
    }
  };

  // Start streaming session
  const startStreamingSession = async () => {
    if (!sessionInfoRef.current) {
      updateStatus("✗ No session info available");
      return;
    }

    if (!roomRef.current) {
      updateStatus("✗ LiveKit room not initialized");
      return;
    }

    try {
      updateStatus("Starting HeyGen streaming...");
      const startResponse = await fetch(
        `${API_CONFIG.serverUrl}/v1/streaming.start`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionTokenRef.current}`,
          },
          body: JSON.stringify({
            session_id: sessionInfoRef.current.session_id,
          }),
        }
      );

      if (!startResponse.ok) {
        throw new Error(`HTTP error! status: ${startResponse.status}`);
      }

      updateStatus("✓ HeyGen streaming started, connecting to LiveKit...");
      
      // Connect to LiveKit room
      const liveKitToken = await generateLiveKitToken();
      await roomRef.current.connect(LIVEKIT_CONFIG.serverUrl, liveKitToken);
      
      updateStatus("✓ Streaming started successfully");
    } catch (error) {
      updateStatus(`✗ Error starting streaming: ${error.message}`);
      throw error;
    }
  };

  // Send text to avatar
  const sendText = async (text, taskType = "talk") => {
    if (!sessionInfoRef.current) {
      updateStatus("✗ No active session");
      return;
    }

    try {
      updateStatus(`Sending text to avatar (${taskType})...`);
      const response = await fetch(
        `${API_CONFIG.serverUrl}/v1/streaming.task`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionTokenRef.current}`,
          },
          body: JSON.stringify({
            session_id: sessionInfoRef.current.session_id,
            text: text,
            task_type: taskType,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      updateStatus(`✓ Sent text (${taskType}): ${text}`);
    } catch (error) {
      updateStatus(`✗ Error sending text: ${error.message}`);
    }
  };

  // Close session
  const closeSession = async () => {
    try {
      if (sessionInfoRef.current && sessionTokenRef.current) {
        updateStatus("Stopping HeyGen session...");
        await fetch(
          `${API_CONFIG.serverUrl}/v1/streaming.stop`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${sessionTokenRef.current}`,
            },
            body: JSON.stringify({
              session_id: sessionInfoRef.current.session_id,
            }),
          }
        );
      }

      // Close WebSocket
      if (webSocketRef.current) {
        webSocketRef.current.close();
        webSocketRef.current = null;
      }
      
      // Disconnect from LiveKit room
      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }

      // Clean up media
      if (mediaElementRef.current) {
        mediaElementRef.current.srcObject = null;
      }
      
      // Reset refs and state
      sessionInfoRef.current = null;
      mediaStreamRef.current = null;
      sessionTokenRef.current = null;
      setIsConnected(false);
      setIsInitialized(false);

      updateStatus("✓ Session closed completely");
    } catch (error) {
      updateStatus(`Error during cleanup: ${error.message}`);
    }
  };

  // Clear logs
  const clearLogs = () => {
    setStatusLogs([]);
    updateStatus("Logs cleared");
  };

  // Start session handler
  const handleStartSession = async () => {
    try {
      await createNewSession();
      await startStreamingSession();
    } catch (error) {
      updateStatus(`✗ Failed to start session: ${error.message}`);
    }
  };

  // Talk button handler
  const handleTalk = () => {
    const text = taskInput.trim();
    if (text) {
      sendText(text, "talk");
      setTaskInput("");
    } else {
      updateStatus("Please enter text to send");
    }
  };

  // Repeat button handler
  const handleRepeat = () => {
    const text = taskInput.trim();
    if (text) {
      sendText(text, "repeat");
      setTaskInput("");
    } else {
      updateStatus("Please enter text to repeat");
    }
  };

  // Video click handler to enable playback
  const handleVideoClick = () => {
    if (mediaElementRef.current) {
      mediaElementRef.current.play().catch(e => {
        console.log("Play failed:", e);
      });
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.mainCard}>
        <div style={styles.warningBox}>
          <p style={styles.warningText}>
            <strong>Setup Required:</strong> Make sure LiveKit client is loaded and credentials are configured.
          </p>
        </div>

        <div style={styles.inputGroup}>
          <input
            type="text"
            placeholder="Avatar ID"
            value={avatarID}
            onChange={(e) => setAvatarID(e.target.value)}
            style={styles.input}
          />
          <input
            type="text"
            placeholder="Voice ID"
            value={voiceID}
            onChange={(e) => setVoiceID(e.target.value)}
            style={styles.input}
          />
          <button
            onClick={handleStartSession}
            disabled={isConnected}
            style={{
              ...styles.button,
              ...styles.buttonGreen,
              ...(isConnected ? styles.buttonDisabled : {})
            }}
          >
            {isInitialized ? 'Starting...' : 'Start'}
          </button>
          <button
            onClick={closeSession}
            style={{...styles.button, ...styles.buttonRed}}
          >
            Close
          </button>
          <button
            onClick={clearLogs}
            style={{...styles.button, backgroundColor: '#6b7280'}}
          >
            Clear Logs
          </button>
        </div>

        <div style={styles.inputGroup}>
          <input
            type="text"
            placeholder="Enter text for avatar to speak"
            value={taskInput}
            onChange={(e) => setTaskInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleTalk();
            }}
            style={styles.input}
          />
          <button
            onClick={handleTalk}
            disabled={!isConnected}
            style={{
              ...styles.button,
              ...styles.buttonGreen,
              ...(!isConnected ? styles.buttonDisabled : {})
            }}
          >
            Talk (LLM)
          </button>
          <button
            onClick={handleRepeat}
            disabled={!isConnected}
            style={{
              ...styles.button,
              ...styles.buttonBlue,
              ...(!isConnected ? styles.buttonDisabled : {})
            }}
          >
            Repeat
          </button>
        </div>

        <video
          ref={mediaElementRef}
          style={styles.video}
          autoPlay
          playsInline
          muted
          onClick={handleVideoClick}
        />

        <div style={styles.statusBox}>
          {statusLogs.length === 0 ? (
            "Status messages will appear here..."
          ) : (
            statusLogs.map((log, index) => (
              <div key={index} style={{ marginBottom: '4px' }}>
                {log}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default HeygenStreamingComponent;